/**
 * Inspired by https://simonsmith.github.io/jquery.stickyTableHeader/
 */
export default class StickyElement {

	private readonly elements: Map<HTMLElement,HTMLElement> = new Map();
	private readonly stickyCopies: Map<HTMLElement,HTMLElement> = new Map();

	private readonly scrollXListener: ()=>void;
	private readonly scrollYListener: ()=>void;
	private readonly resizeListener: ()=>void;

	constructor(
		private readonly scrollYElement: Element|Window,
		private readonly scrollXElement: Element|Window,
		private readonly stickyTarget: HTMLElement,
	) {
		let timeout: NodeJS.Timeout|null = null;

		this.scrollXListener = () => {
			this.processScrollLeft();
		};

		this.scrollYListener = () => {
			if (timeout) {
				return;
			}
			timeout = setTimeout(() => {
				this.processVisibility();
				timeout = null;
			}, 50);
		};

		this.resizeListener = () => {
			this.stickyCopies.forEach((copy,orig) => this.hideStickyCopy(orig));
			this.processVisibility();
		}

		stickyTarget.style.willChange = 'left';
		stickyTarget.style.position = 'relative';

		scrollXElement.addEventListener('scroll', this.scrollXListener);
		scrollYElement.addEventListener('scroll', this.scrollYListener);
		window.addEventListener('resize', this.resizeListener);
	}

	public stick(element: HTMLElement, parent: HTMLElement): void {
		this.elements.set(element, parent);
	}

	public detach(element: HTMLElement): void {
		this.elements.delete(element);
	}

	public destroy(): void {
		this.elements.clear();
		this.scrollXElement.removeEventListener('scroll', this.scrollXListener);
		this.scrollYElement.removeEventListener('scroll', this.scrollYListener);
		window.removeEventListener('resize', this.resizeListener);
	}

	private processScrollLeft(): void {
		// @ts-ignore
		const scrollLeft = this.scrollXElement.scrollX ?? this.scrollXElement.scrollLeft;
		this.stickyTarget.style.left = -scrollLeft+'px';
	}

	private processVisibility(): void {
		this.elements.forEach((parent, element) => {
			if ( this.shouldShowStickyElement(element, parent) ) {
				this.showStickyCopy(element);
			} else {
				this.hideStickyCopy(element);
			}
		});
	}

	private shouldShowStickyElement(element: HTMLElement, parent:HTMLElement): boolean {
		const elementRect = element.getBoundingClientRect();
		const parentRect = parent.getBoundingClientRect();

		return elementRect.top < 0 && elementRect.height - parentRect.height < parentRect.top;
	}

	private showStickyCopy(element: HTMLElement): void {
		if (!this.stickyCopies.has(element)) {
			const stickyCopy = this.createStickyCopy(element);
			this.stickyCopies.set(element, stickyCopy);
			this.stickyTarget.append( stickyCopy );
		}
	}

	private hideStickyCopy(element: HTMLElement): void {
		if ( this.stickyCopies.has(element) ) {
			const stickyCopy = this.stickyCopies.get(element);
			this.stickyCopies.delete(element);
			stickyCopy?.remove();
		}
	}

	private createStickyCopy(original: HTMLElement): HTMLElement {

		const traverse = (cp: HTMLElement, orig: HTMLElement) => {
			cp.style.width = orig.getBoundingClientRect().width+'px';
            const computedStyle = window.getComputedStyle(orig);
			cp.style.padding = computedStyle.getPropertyValue('padding');
			cp.style.margin = computedStyle.getPropertyValue('margin');
			cp.style.display = computedStyle.getPropertyValue('display');
			cp.style.fontSize = computedStyle.getPropertyValue('font-size');
			cp.style.lineHeight = computedStyle.getPropertyValue('line-height');

			const cpChildren = <HTMLElement[]>Array.from(cp.children);
			const origChildren = <HTMLElement[]>Array.from(orig.children);
			cpChildren.forEach((cpChild, i) => traverse(cpChild, origChildren[i]));
		}

		const copy = <HTMLElement>original.cloneNode(true);
		traverse(copy, original);

		return copy;
	}

}
